import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/api';
import {
  ArrowLeft, RefreshCw, CheckCircle2, AlertTriangle, Wifi, Loader2,
  Users, Ticket, Clock,
} from 'lucide-react';

/**
 * Fullscreen live event check-in dashboard. URL: `/admin/events/:id/live`.
 *
 * Designed to run on a tablet/monitor at the door during an event.
 * Polls `/api/admin/events/{id}/live-stats` every 5s. Big numbers,
 * tier breakdown, last 25 check-ins feed, and a fill-pct alert banner
 * at >85% (yellow) and >95% (red).
 */
export default function AdminEventLiveDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [pulseLast, setPulseLast] = useState(null);
  const lastSeenCheckinRef = useRef(null);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await adminApi.get(`/admin/events/${id}/live-stats`);
      setData(r.data);
      setError(null);
      setUpdatedAt(new Date());
      // Pulse animation when a NEW check-in lands.
      const last = r.data?.last_checkin_at || null;
      if (last && last !== lastSeenCheckinRef.current) {
        if (lastSeenCheckinRef.current !== null) {
          setPulseLast(last);
          setTimeout(() => setPulseLast(null), 1800);
        }
        lastSeenCheckinRef.current = last;
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore nel caricamento');
    }
  }, [id]);

  useEffect(() => {
    document.title = 'Live Check-in — Lazo';
    load();
    timerRef.current = setInterval(load, 5000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  if (!data && !error) {
    return (
      <div className="fixed inset-0 bg-[#0B1220] flex items-center justify-center" data-testid="live-loading">
        <Loader2 className="animate-spin text-[#3ECF8E]" size={42} />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="fixed inset-0 bg-[#0B1220] flex flex-col items-center justify-center text-white p-8 text-center">
        <p className="text-2xl font-bold">{error}</p>
        <button onClick={() => navigate('/admin/events-tickets')}
          className="mt-6 px-5 py-2.5 rounded-xl bg-[#3ECF8E] text-[#0B1220] text-sm font-bold">
          Torna alla biglietteria
        </button>
      </div>
    );
  }

  const ev = data.event;
  const fill = data.fill_pct || 0;
  const isHigh = fill >= 85 && fill < 95;
  const isCritical = fill >= 95;
  const accent = isCritical ? '#EF4444' : isHigh ? '#FACC15' : '#3ECF8E';
  const cap = data.capacity_total;

  const fmtTime = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
    catch { return ''; }
  };

  return (
    <div className="fixed inset-0 bg-[#0B1220] text-white overflow-y-auto" data-testid="live-dashboard">
      {/* Top bar */}
      <header className="sticky top-0 z-10 bg-[#0B1220]/95 backdrop-blur border-b border-white/5">
        <div className="max-w-screen-2xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/admin/events-tickets')} data-testid="live-back-btn"
            className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center">
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/40">Live · Check-in evento</p>
            <h1 className="text-2xl md:text-3xl font-black truncate">{ev.name}</h1>
            <p className="text-xs text-white/50 mt-0.5">
              {[ev.event_date, ev.event_time, ev.location].filter(Boolean).join(' · ')}
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-white/40">
            <Wifi size={12} className="text-emerald-400" />
            <span className="tabular-nums">
              {updatedAt ? `Aggiornato ${updatedAt.toLocaleTimeString('it-IT')}` : '—'}
            </span>
            <button onClick={load} className="ml-2 w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center">
              <RefreshCw size={13} />
            </button>
          </div>
        </div>
      </header>

      {/* Alert banners */}
      {(isHigh || isCritical) && (
        <div className="max-w-screen-2xl mx-auto px-6 pt-4">
          <div className="rounded-2xl px-5 py-3 flex items-center gap-3 font-bold text-sm"
            style={{ backgroundColor: `${accent}25`, color: accent, border: `1px solid ${accent}50` }}
            data-testid={isCritical ? "alert-critical" : "alert-high"}>
            <AlertTriangle size={18} />
            {isCritical
              ? 'Capienza al limite. Valuta di chiudere gli ingressi.'
              : 'Sala oltre 85% di riempimento. Tieni d’occhio gli ingressi.'}
          </div>
        </div>
      )}

      <main className="max-w-screen-2xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT: Big numbers + tier breakdown */}
        <section className="lg:col-span-8 space-y-6">
          {/* Hero KPI */}
          <div className="rounded-3xl p-8 md:p-10 border border-white/10 relative overflow-hidden"
            style={{ background: `radial-gradient(circle at 0% 0%, ${accent}22, transparent 70%), #0F1A2E` }}
            data-testid="hero-kpi">
            <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 mb-3">Entrati / Venduti</p>
            <div className="flex items-baseline gap-4 flex-wrap">
              <p className="text-7xl md:text-8xl font-black tabular-nums leading-none" style={{ color: accent }} data-testid="kpi-checked-in">
                {data.checked_in}
              </p>
              <p className="text-4xl md:text-5xl font-black tabular-nums text-white/30">
                / {data.sold}
              </p>
              {cap && (
                <p className="text-base text-white/50 ml-2">capienza {cap}</p>
              )}
            </div>

            {/* Big fill bar */}
            <div className="mt-6">
              <div className="flex justify-between text-xs text-white/50 mb-2">
                <span>Riempimento</span>
                <span className="font-bold tabular-nums" style={{ color: accent }} data-testid="kpi-fill-pct">{fill}%</span>
              </div>
              <div className="h-4 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${Math.min(100, fill)}%`, backgroundColor: accent }} />
              </div>
            </div>

            {/* Mini stats */}
            <div className="grid grid-cols-3 gap-3 mt-7">
              <MiniStat icon={<Users size={14} />} label="Entrati" value={data.checked_in} accent="#3ECF8E" testid="mini-entered" />
              <MiniStat icon={<Clock size={14} />} label="Ancora fuori" value={data.outside} accent="#FACC15" testid="mini-outside" />
              <MiniStat icon={<Ticket size={14} />} label="Venduti" value={data.sold} accent="#60A5FA" testid="mini-sold" />
            </div>
          </div>

          {/* Tier breakdown */}
          <div className="rounded-3xl p-6 md:p-7 border border-white/10 bg-[#0F1A2E]" data-testid="tier-breakdown">
            <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 mb-4">Per fascia</p>
            <div className="space-y-3">
              {(data.by_tier || []).map(t => {
                const cap = t.capacity;
                const denom = cap || t.sold || 1;
                const pct = denom ? Math.round((t.checked_in / denom) * 100) : 0;
                const tAccent = pct >= 95 ? '#EF4444' : pct >= 85 ? '#FACC15' : '#3ECF8E';
                return (
                  <div key={t.name} className="rounded-2xl bg-white/3 border border-white/5 p-4" data-testid={`tier-${t.name}`}>
                    <div className="flex items-baseline justify-between gap-3 mb-2">
                      <p className="text-base md:text-lg font-bold">{t.name}</p>
                      <p className="text-2xl md:text-3xl font-black tabular-nums" style={{ color: tAccent }}>
                        {t.checked_in}<span className="text-white/30 text-base font-bold"> / {t.sold}{cap ? ` · cap ${cap}` : ''}</span>
                      </p>
                    </div>
                    <div className="h-2.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${Math.min(100, pct)}%`, backgroundColor: tAccent }} />
                    </div>
                  </div>
                );
              })}
              {(!data.by_tier || data.by_tier.length === 0) && (
                <p className="text-sm text-white/40 italic">Nessuna fascia configurata</p>
              )}
            </div>
          </div>
        </section>

        {/* RIGHT: Live feed */}
        <aside className="lg:col-span-4">
          <div className="rounded-3xl border border-white/10 bg-[#0F1A2E] p-5 lg:sticky lg:top-24" data-testid="live-feed">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/40">Ultimi check-in</p>
              <span className="flex items-center gap-1.5 text-[10px] text-emerald-400 font-bold">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                LIVE
              </span>
            </div>
            {(data.recent_checkins || []).length === 0 ? (
              <p className="text-sm text-white/40 italic mt-2">Nessun check-in ancora.</p>
            ) : (
              <ul className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
                {data.recent_checkins.map(c => {
                  const isPulse = pulseLast && c.checked_in_at === pulseLast;
                  return (
                    <li key={c.id} data-testid={`feed-${c.id}`}
                      className={`rounded-xl px-3 py-2.5 border transition-all ${
                        isPulse ? 'bg-emerald-500/15 border-emerald-500/40 scale-[1.01]' : 'bg-white/3 border-white/5'
                      }`}>
                      <div className="flex items-start gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle2 size={14} className="text-emerald-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate">{c.customer_name}</p>
                          <p className="text-[11px] text-white/50 truncate">
                            {c.tier_name}
                            {c.by_staff ? ` · ${c.by_staff}` : ''}
                          </p>
                        </div>
                        <span className="text-[10px] text-white/40 tabular-nums flex-shrink-0">{fmtTime(c.checked_in_at)}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>
      </main>
    </div>
  );
}

function MiniStat({ icon, label, value, accent, testid }) {
  return (
    <div className="rounded-2xl bg-white/3 border border-white/5 p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-white/40">
        <span style={{ color: accent }}>{icon}</span>
        <span className="text-[10px] uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <p className="text-2xl md:text-3xl font-black tabular-nums mt-1" style={{ color: accent }}>{value}</p>
    </div>
  );
}
